import { formatDistanceToNow } from 'date-fns'
import { CalendarClock } from 'lucide-react'
import { useState } from 'react'
import { RescheduleDialog } from '@/components/layout/oci-migration-notice/migration-schedule/reschedule-dialog'
import {
  formatMigrationDate,
  formatUtcTime,
} from '@/components/layout/oci-migration-notice/migration-schedule/utils'
import { COMPANY_MIGRATION_SCHEDULE_QUERY } from '@/components/layout/oci-migration-notice/queries'
import { Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { useRBAC, useSuspenseQuery } from '@/lib/hooks'
import type {
  CompanyClusterMigrationScheduleQuery,
  CompanyClusterMigrationScheduleQueryVariables,
} from '@/components/layout/oci-migration-notice/queries.types.gen'
import type { ClusterMigrationScheduleStatus } from '@/graphql.types'

type Schedule = NonNullable<
  CompanyClusterMigrationScheduleQuery['companyClusterMigrationSchedule']
>

const STATUS_TITLES: Partial<Record<ClusterMigrationScheduleStatus, string>> = {
  unscheduled: 'No date yet',
  onHold: 'On hold',
  started: 'In progress',
  completed: 'Completed',
}

const STATUS_DESCRIPTIONS: Partial<
  Record<ClusterMigrationScheduleStatus, string>
> = {
  scheduled:
    'Your services keep running while we migrate them. From the start until the migration finishes, deployments and other changes are paused and git pushes are not deployed. Some services may be unavailable for a short time when we switch over.',
  unscheduled: 'We will email you as soon as your migration has a date.',
  onHold:
    'Your migration is on hold. We will email you as soon as it has a new date.',
  started:
    'We are moving your services now. They keep running, but deployments and other changes are paused until the migration finishes. Git pushes are not deployed, so deploy again afterwards.',
  completed: 'Your services now run on Oracle Cloud Infrastructure.',
}

const getReasonLabel = (reason: Schedule['reason']) => {
  if (reason === 'customerRequest') {
    return 'You picked this time'
  }

  return reason === 'admin' ? 'Set by our team' : null
}

const ScheduleDate = ({ schedule }: { schedule: Schedule }) => {
  const { status, scheduledAt, reason } = schedule

  if (status !== 'scheduled' || !scheduledAt) {
    return (
      <span className="text-base font-semibold">
        {STATUS_TITLES[status] ?? 'No date yet'}
      </span>
    )
  }

  const reasonLabel = getReasonLabel(reason)

  return (
    <div className="flex flex-col gap-0.5">
      <time dateTime={scheduledAt} className="text-base font-semibold">
        {formatMigrationDate(scheduledAt)}
      </time>
      <span className="text-xs text-muted-foreground">
        {formatUtcTime(scheduledAt)} ·{' '}
        {formatDistanceToNow(new Date(scheduledAt), { addSuffix: true })}
        {reasonLabel ? ` · ${reasonLabel}` : null}
      </span>
    </div>
  )
}

type MigrationScheduleContentProps = {
  idCompany: string
  waveLabel: string
}

export const MigrationScheduleContent = ({
  idCompany,
  waveLabel,
}: MigrationScheduleContentProps) => {
  const { rbac } = useRBAC()
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false)

  const { data } = useSuspenseQuery<
    CompanyClusterMigrationScheduleQuery,
    CompanyClusterMigrationScheduleQueryVariables
  >(COMPANY_MIGRATION_SCHEDULE_QUERY, { variables: { idCompany } })

  const schedule = data?.companyClusterMigrationSchedule

  if (!schedule || schedule.status === 'cancelled') {
    return (
      <Muted as="p" className="text-xs">
        Expect further communication from us via email about the start of{' '}
        {waveLabel}.
      </Muted>
    )
  }

  const { status, scheduledAt, canReschedule, targetClusters } = schedule
  const canEdit = rbac.company(idCompany).apps.update.can()
  const hasDate = status === 'scheduled' && Boolean(scheduledAt)
  const targetNames = targetClusters.map(
    ({ displayName, name }) => displayName || name
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-lg border p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <ScheduleDate schedule={schedule} />
          {canReschedule && canEdit ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsRescheduleOpen(true)}
            >
              <CalendarClock />
              {hasDate ? 'Pick another time' : 'Pick a time'}
            </Button>
          ) : null}
        </div>
        <Muted as="p" className="text-xs">
          {STATUS_DESCRIPTIONS[status]}
          {status === 'scheduled' && targetNames.length
            ? ` Your services move to ${targetNames.join(', ')}.`
            : null}
        </Muted>
        {canReschedule && !canEdit ? (
          <Muted as="p" className="text-xs">
            Ask a company owner or admin if you need another time.
          </Muted>
        ) : null}
      </div>
      {canReschedule && canEdit ? (
        <RescheduleDialog
          idCompany={idCompany}
          waveLabel={waveLabel}
          hasDate={hasDate}
          isOpen={isRescheduleOpen}
          onOpenChange={setIsRescheduleOpen}
        />
      ) : null}
    </div>
  )
}
